import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { KnowledgeEvidenceSource } from "@repo/shared";
import {
  DOCUMENT_EVIDENCE_FIGURE_IMAGE_LIMIT,
  DOCUMENT_EVIDENCE_PRESENTATION_LIMIT,
  type DocumentEvidenceOwner,
  type DocumentEvidencePresentation,
  type DocumentEvidenceReference,
} from "@repo/shared/document-evidence-link";
import { useConvex, useQuery } from "convex/react";
import type { FunctionArgs } from "convex/server";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  findLiveEvidenceAnchor,
  isEvidenceAnchorLive,
  readEvidenceAnchorOrdinal,
} from "./document-evidence-dom";
import {
  buildCitationRows,
  buildFigureBlockKey,
  buildKnowledgeSourceRows,
  buildLocatorArgs,
  buildLocatorKey,
  buildSourceUrlKey,
  chunkForRequest,
  type EvidenceLocatorArgs,
  type EvidenceSourceRequest,
  isSamePresentation,
  type KnowledgeSourceRowContext,
  presentedReference,
} from "./evidence-citation-rows";
import {
  type EvidenceCitationRow,
  EvidenceCitationsPopover,
  type EvidenceFigureImageUrls,
} from "./evidence-citations-popover";
import type { DocumentEvidenceCitation } from "./evidence-runs";
import { useEvidenceSourceUrls } from "./use-evidence-source-urls";

interface DocumentEvidenceInteractionContextValue {
  projectId: Id<"projects">;
  /**
   * Opens the citation list for a whole run of adjacent markers, anchored to the marker
   * the reader activated. Every citation surface — entity content, the chat transcript,
   * the message footer, ask-user questions, the Knowledge screen — opens through here, so a
   * citation reads the same everywhere. Group state lives in provider state, never in this
   * value: the DOM delegation layer re-attaches its listeners whenever the value identity
   * changes.
   *
   * `knowledgeSources` are the sources a knowledge item cites that are not documents — a
   * decision, a closed clarification, somebody typing an entry. They carry no locator and
   * nothing to resolve, so a group may consist of them alone. `knowledgeSourceContext` is what
   * only the citing screen knows: the item's own text, the record ids beside each source, and
   * how to address a clarification thread.
   */
  activateEvidenceGroup: (
    citations: readonly DocumentEvidenceCitation[],
    anchor: HTMLElement,
    options?: {
      knowledgeSources?: readonly KnowledgeEvidenceSource[];
      knowledgeSourceContext?: KnowledgeSourceRowContext;
      /** Whose citations these are, so the list shows what became of each for its owner. */
      owner?: DocumentEvidenceOwner | null;
    }
  ) => void;
  registerLocator: (locator: EvidenceLocatorArgs) => () => void;
}

interface ActiveEvidenceGroup {
  anchor: HTMLElement;
  /** Where `anchor` stood among the markers for its citation when it was last seen live. */
  anchorOrdinal: number;
  citations: readonly DocumentEvidenceCitation[];
  knowledgeSources: readonly KnowledgeEvidenceSource[];
  knowledgeSourceContext: KnowledgeSourceRowContext;
  owner: DocumentEvidenceOwner | null;
}

interface RegistryEntry {
  locator: EvidenceLocatorArgs;
  refCount: number;
}

interface LocatorBatch {
  id: string;
  keys: readonly string[];
  locators: readonly EvidenceLocatorArgs[];
}

type PresentationMap = ReadonlyMap<string, DocumentEvidencePresentation>;

const EMPTY_PRESENTATIONS: PresentationMap = new Map();

/**
 * How long a cited figure's resolved image URL is trusted before the next open re-mints it.
 * It is signed with roughly an hour of validity; refreshing well inside that keeps a
 * thumbnail from turning into a broken image in a long-lived keep-alive session.
 */
const EVIDENCE_URL_REFRESH_MS = 10 * 60 * 1000;

const DocumentEvidenceInteractionContext =
  createContext<DocumentEvidenceInteractionContextValue | null>(null);

/**
 * Kept separate from the interaction context so that presentation updates never change
 * the identity of the stable `{ projectId, activateEvidenceGroup, registerLocator }` value —
 * `useEditorMentionInteractions` re-attaches its DOM listeners whenever that changes.
 */
const DocumentEvidencePresentationContext = createContext<PresentationMap>(EMPTY_PRESENTATIONS);

/**
 * Whose citations the chips below cite: the element whose content holds them, or the knowledge
 * item. A citation's carry-over state belongs to its owner, so chips outside any owner — the chat
 * transcript, whose citations stay on the version Claudia read — present none.
 */
const DocumentEvidenceOwnerContext = createContext<DocumentEvidenceOwner | null>(null);

export function DocumentEvidenceOwnerProvider({
  owner,
  children,
}: PropsWithChildren<{ owner: DocumentEvidenceOwner | null }>) {
  const ownerKey = owner
    ? `${owner.kind}:${owner.kind === "element" ? owner.entityId : owner.claimId}`
    : "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: the key is the owner's identity, so a fresh object for the same owner keeps the value.
  const value = useMemo(() => owner, [ownerKey]);
  return (
    <DocumentEvidenceOwnerContext.Provider value={value}>
      {children}
    </DocumentEvidenceOwnerContext.Provider>
  );
}

export function useDocumentEvidenceOwner(): DocumentEvidenceOwner | null {
  return useContext(DocumentEvidenceOwnerContext);
}

type PresentLocator = FunctionArgs<typeof api.documentEvidence.present>["locators"][number];

/** A registered locator as the presentation query takes it, its owner's id typed as a row id. */
function toPresentLocator({ owner, ...locator }: EvidenceLocatorArgs): PresentLocator {
  if (!owner) return locator;
  return {
    ...locator,
    owner:
      owner.kind === "element"
        ? { kind: "element", entityId: owner.entityId as Id<"entities"> }
        : { kind: "knowledge", claimId: owner.claimId as Id<"claims"> },
  };
}

/**
 * Subscribes to one batch of locators. Rendered as a sibling of `children` so the batch
 * count can change without re-parenting (and therefore remounting) the editor tree.
 */
function DocumentEvidenceBatchSubscription({
  projectId,
  batchKeys,
  locators,
  onResults,
}: {
  projectId: Id<"projects">;
  batchKeys: readonly string[];
  locators: readonly EvidenceLocatorArgs[];
  onResults: (keys: readonly string[], values: readonly DocumentEvidencePresentation[]) => void;
}) {
  const args = useMemo(
    () => ({ projectId, locators: locators.map(toPresentLocator) }),
    [locators, projectId]
  );
  const result = useQuery(api.documentEvidence.present, args);

  useEffect(() => {
    // Publish only once the subscription answers. Registering one more chip re-chunks the
    // batches into a query the client has not subscribed to yet, so `result` is briefly
    // undefined; clearing on that would flash every already-resolved chip in the batch
    // back to its loading state each time a streaming message adds another one. Entries
    // are pruned by the provider when their locator leaves the registry instead.
    if (!result) return;
    onResults(batchKeys, result);
  }, [batchKeys, onResults, result]);

  return null;
}

export function DocumentEvidenceInteractionProvider({
  projectId,
  children,
}: PropsWithChildren<{ projectId: Id<"projects"> }>) {
  const convex = useConvex();
  const [activeGroup, setActiveGroup] = useState<ActiveEvidenceGroup | null>(null);
  const [registry, setRegistry] = useState<ReadonlyMap<string, RegistryEntry>>(() => new Map());
  const [presentations, setPresentations] = useState<PresentationMap>(EMPTY_PRESENTATIONS);
  const [figureImageUrls, setFigureImageUrls] = useState<
    ReadonlyMap<string, EvidenceFigureImageUrls>
  >(() => new Map());
  const requestedFigureBlocksAt = useRef<Map<string, number>>(new Map());
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const registerLocator = useCallback((locator: EvidenceLocatorArgs) => {
    const key = buildLocatorKey(locator);
    setRegistry((previous) => {
      const next = new Map(previous);
      const existing = next.get(key);
      next.set(key, { locator, refCount: (existing?.refCount ?? 0) + 1 });
      return next;
    });

    let released = false;
    return () => {
      if (released) return;
      released = true;
      setRegistry((previous) => {
        const existing = previous.get(key);
        if (!existing) return previous;
        const next = new Map(previous);
        if (existing.refCount <= 1) {
          next.delete(key);
        } else {
          next.set(key, { ...existing, refCount: existing.refCount - 1 });
        }
        return next;
      });
    };
  }, []);

  const activateEvidenceGroup = useCallback(
    (
      citations: readonly DocumentEvidenceCitation[],
      anchor: HTMLElement,
      options?: {
        knowledgeSources?: readonly KnowledgeEvidenceSource[];
        knowledgeSourceContext?: KnowledgeSourceRowContext;
        owner?: DocumentEvidenceOwner | null;
      }
    ) => {
      const knowledgeSources = options?.knowledgeSources ?? [];
      if (citations.length === 0 && knowledgeSources.length === 0) return;
      // A save can race the click and redraw the marker before this state lands; opening
      // against a dead anchor would position the popover at the viewport origin.
      const liveAnchor = findLiveEvidenceAnchor(anchor, anchor.ownerDocument);
      if (!liveAnchor) return;
      setActiveGroup({
        anchor: liveAnchor,
        anchorOrdinal: readEvidenceAnchorOrdinal(liveAnchor, liveAnchor.ownerDocument),
        citations,
        knowledgeSources,
        knowledgeSourceContext: options?.knowledgeSourceContext ?? {},
        owner: options?.owner ?? null,
      });
    },
    []
  );

  // The anchor is a DOM node captured at click time, and the surface it lives in keeps
  // rendering under the open popover: ProseMirror replaces the document when an external
  // save lands, the transcript re-renders while the assistant streams. A detached or hidden
  // anchor has an all-zero rect, which Floating UI positions at the viewport's top-left
  // corner (LAT-980). Follow the citation to the marker drawn in its place, and close when
  // nothing stands for it any more.
  useEffect(() => {
    if (!activeGroup) return;
    const { anchor, anchorOrdinal } = activeGroup;
    const root = anchor.ownerDocument;
    const follow = () => {
      if (isEvidenceAnchorLive(anchor)) return;
      const replacement = findLiveEvidenceAnchor(anchor, root, anchorOrdinal);
      setActiveGroup((current) => {
        if (current?.anchor !== anchor) return current;
        return replacement ? { ...current, anchor: replacement } : null;
      });
    };
    // The marker can also be redrawn between activation's liveness check and this effect
    // (a surface re-render later in the same commit); the observer would miss that
    // mutation, so check once at install.
    follow();
    const observer = new MutationObserver(follow);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });
    return () => observer.disconnect();
  }, [activeGroup]);

  const value = useMemo<DocumentEvidenceInteractionContextValue>(
    () => ({ projectId, activateEvidenceGroup, registerLocator }),
    [activateEvidenceGroup, projectId, registerLocator]
  );

  // Pin the locators of an open citation list: the markers behind the popover may unmount
  // (a keep-alive tab switch, a re-render of the editor) while the reader still reads it.
  const activeCitations = activeGroup?.citations ?? null;
  const activeOwner = activeGroup?.owner ?? null;
  useEffect(() => {
    if (!activeCitations) return;
    const releases = activeCitations
      .map((citation) => buildLocatorArgs(citation.reference, activeOwner))
      .filter((locator): locator is EvidenceLocatorArgs => locator !== null)
      .map(registerLocator);
    return () => {
      for (const release of releases) release();
    };
  }, [activeCitations, activeOwner, registerLocator]);

  // Registered locators are chunked to the limit the resolver accepts, so one document or
  // message surface opens a single subscription instead of one per citation.
  const batches = useMemo<LocatorBatch[]>(() => {
    const keys = [...registry.keys()].sort();
    const result: LocatorBatch[] = [];
    for (let index = 0; index < keys.length; index += DOCUMENT_EVIDENCE_PRESENTATION_LIMIT) {
      const batchKeys = keys.slice(index, index + DOCUMENT_EVIDENCE_PRESENTATION_LIMIT);
      result.push({
        id: batchKeys[0] ?? String(index),
        keys: batchKeys,
        locators: batchKeys.map((key) => registry.get(key)!.locator),
      });
    }
    return result;
  }, [registry]);

  const applyBatchResults = useCallback(
    (keys: readonly string[], values: readonly DocumentEvidencePresentation[]) => {
      setPresentations((previous) => {
        const next = new Map(previous);
        let changed = false;
        keys.forEach((key, index) => {
          const presentation = values[index];
          if (!presentation) return;
          const current = next.get(key);
          if (!current || !isSamePresentation(current, presentation)) {
            next.set(key, presentation);
            changed = true;
          }
        });
        return changed ? next : previous;
      });
    },
    []
  );

  // Drop presentations whose locator no longer has a live marker or open citation list.
  useEffect(() => {
    setPresentations((previous) => {
      const next = new Map(previous);
      let changed = false;
      for (const key of previous.keys()) {
        if (!registry.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [registry]);

  // Which sources currently need an openable URL: whatever the reader has open, named by the
  // revision that cites them so two cited versions of one document open two files — less the
  // recordings. A transcribed recording's source is read in the transcript viewer, and its
  // openable URL is a presigned link to the audio that nothing would use, so a source whose
  // every resolved citation says "transcript" is not signed at all. A citation that has not
  // resolved yet says nothing about the source, and is minted as any other is.
  const wantedSources = useMemo(() => {
    const byKey = new Map<string, { source: EvidenceSourceRequest; wanted: boolean }>();
    for (const citation of activeGroup?.citations ?? []) {
      const locator = buildLocatorArgs(citation.reference, activeGroup?.owner);
      const presentation = locator ? presentations.get(buildLocatorKey(locator)) : undefined;
      // A forwarded citation opens the newest version's file, not the one it was written against.
      const { artifactId, processingRevisionId } = presentedReference(
        citation.reference,
        presentation
      );
      if (!artifactId) continue;
      const source: EvidenceSourceRequest = {
        artifactId,
        ...(processingRevisionId ? { processingRevisionId } : {}),
      };
      const key = buildSourceUrlKey(source);
      const isTranscript = presentation?.status === "available" && presentation.transcript;
      byKey.set(key, {
        source,
        wanted: (byKey.get(key)?.wanted ?? false) || !isTranscript,
      });
    }
    return [...byKey.values()].filter((entry) => entry.wanted).map((entry) => entry.source);
  }, [activeGroup, presentations]);
  const sourceUrls = useEvidenceSourceUrls(projectId, wantedSources);

  // Which figure images the open citation list needs, keyed by block rather than by passage:
  // two citations of different spans of one figure are shown the single image that block
  // renders, so they must not mint it — or store it — twice. Only an already-presented
  // citation qualifies, because `present` is what says the block is a figure at all.
  const wantedFigureBlocks = useMemo(() => {
    const wanted = new Map<string, EvidenceLocatorArgs>();
    for (const citation of activeGroup?.citations ?? []) {
      const cited = buildLocatorArgs(citation.reference, activeGroup?.owner);
      if (!cited) continue;
      const presentation = presentations.get(buildLocatorKey(cited));
      if (presentation?.status !== "available" || !presentation.figure) continue;
      const locator = buildLocatorArgs(presentedReference(citation.reference, presentation));
      if (locator) wanted.set(buildFigureBlockKey(locator), locator);
    }
    return wanted;
  }, [activeGroup, presentations]);

  /*
   * The figure counterpart of the source-URL resolution above, under the same rules: minted
   * when a reader opens a citation group rather than inside the long-lived `present`
   * subscription, ledgered so a re-run within the refresh window is a no-op, and aged out
   * because storage URLs are signed with an expiry. The wanted set is rebuilt whenever the
   * active group or its presentations change identity; the ledger is what stops that from
   * becoming a fetch loop. Source URLs follow the same rules in `useEvidenceSourceUrls`.
   */
  useEffect(() => {
    const now = Date.now();
    const pending = [...wantedFigureBlocks.entries()].filter(([blockKey]) => {
      const requestedAt = requestedFigureBlocksAt.current.get(blockKey);
      return requestedAt === undefined || now - requestedAt > EVIDENCE_URL_REFRESH_MS;
    });
    if (pending.length === 0) return;
    for (const [blockKey] of pending) requestedFigureBlocksAt.current.set(blockKey, now);

    // Deliberately not cancelled on dependency change: the ledger already makes a re-run a
    // no-op, so a re-render between issuing the query and its answer must not discard it.
    for (const batch of chunkForRequest(pending, DOCUMENT_EVIDENCE_FIGURE_IMAGE_LIMIT)) {
      void Promise.resolve(
        convex.query(api.documentEvidence.resolveFigureImageUrls, {
          projectId,
          // Spans are stripped: the resolver answers per block, and echoes the block identity
          // back so the answer can be filed under the same key it was asked for.
          locators: batch.map(([, locator]) => ({
            artifactId: locator.artifactId,
            processingRevisionId: locator.processingRevisionId,
            blockId: locator.blockId,
          })),
        })
      )
        .then((results) => {
          if (!isMounted.current || !Array.isArray(results)) return;
          setFigureImageUrls((previous) => {
            const next = new Map(previous);
            for (const result of results) {
              next.set(buildFigureBlockKey(result), {
                thumbnailUrl: result.thumbnailUrl,
                fullUrl: result.fullUrl,
              });
            }
            return next;
          });
        })
        .catch(() => {
          // Losing the lookup costs only the thumbnail; the name, excerpt and location all
          // still render. Allow a retry on the next open.
          for (const [blockKey] of batch) requestedFigureBlocksAt.current.delete(blockKey);
        });
    }
  }, [convex, projectId, wantedFigureBlocks]);

  const citationRows = useMemo<EvidenceCitationRow[]>(
    () => [
      ...buildCitationRows({
        citations: activeGroup?.citations ?? [],
        figureImageUrls,
        owner: activeGroup?.owner ?? null,
        presentations,
        sourceUrls,
      }),
      // Appended after the citations, in the order the item cites them: a knowledge source
      // that is not a document arrives already resolved and has no locator to key on.
      ...buildKnowledgeSourceRows(
        activeGroup?.knowledgeSources ?? [],
        activeGroup?.knowledgeSourceContext ?? {}
      ),
    ],
    [activeGroup, figureImageUrls, presentations, sourceUrls]
  );

  return (
    <DocumentEvidenceInteractionContext.Provider value={value}>
      <DocumentEvidencePresentationContext.Provider value={presentations}>
        {children}
        {batches.map((batch) => (
          <DocumentEvidenceBatchSubscription
            key={batch.id}
            projectId={projectId}
            batchKeys={batch.keys}
            locators={batch.locators}
            onResults={applyBatchResults}
          />
        ))}
        <EvidenceCitationsPopover
          anchor={activeGroup?.anchor ?? null}
          open={activeGroup !== null}
          projectId={projectId}
          rows={citationRows}
          onOpenChange={(open) => {
            if (!open) setActiveGroup(null);
          }}
        />
      </DocumentEvidencePresentationContext.Provider>
    </DocumentEvidenceInteractionContext.Provider>
  );
}

export function useOptionalDocumentEvidenceInteraction() {
  return useContext(DocumentEvidenceInteractionContext);
}

/**
 * Registers every usable locator in `references` with the surrounding provider in one
 * effect and returns their share of the batched presentation result, positionally: for
 * each reference `undefined` while loading, and `null` when there is no provider or the
 * reference is not a usable locator.
 *
 * A caller with a variable number of citations (a Markdown run, a citation footer) rebuilds
 * its reference array on every render, so registration is keyed on the locators themselves
 * rather than on the identity of the array holding them.
 */
export function useDocumentEvidencePresentations(
  references: readonly (DocumentEvidenceReference | null)[]
): (DocumentEvidencePresentation | null | undefined)[] {
  const interaction = useContext(DocumentEvidenceInteractionContext);
  const presentations = useContext(DocumentEvidencePresentationContext);
  const owner = useContext(DocumentEvidenceOwnerContext);
  const registerLocator = interaction?.registerLocator;

  const locatorsKey = references
    .map((reference) => {
      const locator = buildLocatorArgs(reference, owner);
      return locator ? buildLocatorKey(locator) : "";
    })
    .join("\n");

  // biome-ignore lint/correctness/useExhaustiveDependencies: locatorsKey *is* the identity of `references` and their owner — a fresh array of the same citations must not re-register them.
  const locators = useMemo(
    () => references.map((reference) => buildLocatorArgs(reference, owner)),
    [locatorsKey]
  );

  useEffect(() => {
    if (!registerLocator) return;
    // `registerLocator` is a plain function, so one effect can hold a whole run: every
    // locator is registered here and released together when the run changes or unmounts.
    const releases = locators
      .filter((locator): locator is EvidenceLocatorArgs => locator !== null)
      .map(registerLocator);
    return () => {
      for (const release of releases) release();
    };
  }, [locators, registerLocator]);

  return locators.map((locator) => {
    if (!interaction || !locator) return null;
    const key = buildLocatorKey(locator);
    return presentations.has(key) ? presentations.get(key) : undefined;
  });
}

/**
 * Registers `reference` with the surrounding provider and returns its share of the
 * batched presentation result: `undefined` while loading, `null` when there is no
 * provider or the reference is not a usable locator.
 */
export function useDocumentEvidencePresentation(
  reference: DocumentEvidenceReference | null
): DocumentEvidencePresentation | null | undefined {
  // One entry in, one entry out — `undefined` here still means "loading", never "missing".
  const [presentation] = useDocumentEvidencePresentations([reference]);
  return presentation;
}
